import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { LevelUpSession } from '../types/levelUp';

/**
 * Firebase integration utilities for Level-Up wizard sessions
 * Stores draft level-ups in a subcollection for persistence across sessions
 */

/**
 * Gets the Firestore reference for a character's level-up draft
 * Path: /users/{userId}/characters/{characterId}/levelUpDrafts/current
 */
function getLevelUpDraftRef(characterId: string) {
  const userId = auth.currentUser?.uid;

  if (!userId) {
    throw new Error('User must be authenticated to access level-up drafts');
  }

  return doc(db, 'users', userId, 'characters', characterId, 'levelUpDrafts', 'current');
}

/**
 * Saves a level-up session to Firestore
 * Stored in subcollection: /users/{userId}/characters/{characterId}/levelUpDrafts/current
 */
export async function saveLevelUpSessionToFirebase(
  session: LevelUpSession
): Promise<void> {
  if (!session.characterId) {
    throw new Error('Cannot save level-up session: no character ID');
  }

  try {
    const draftRef = getLevelUpDraftRef(session.characterId);

    // Save the session data
    await setDoc(draftRef, {
      ...session,
      lastModified: Date.now()
    });

    console.log('[LevelUp] Session saved to Firebase:', session.sessionId);
  } catch (error) {
    console.error('[LevelUp] Failed to save session to Firebase:', error);
    throw error;
  }
}

/**
 * Loads a level-up session from Firestore
 * Returns null if no draft exists
 */
export async function loadLevelUpSessionFromFirebase(
  characterId: string
): Promise<LevelUpSession | null> {
  if (!characterId) {
    return null;
  }

  try {
    const draftRef = getLevelUpDraftRef(characterId);
    const snapshot = await getDoc(draftRef);

    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data() as LevelUpSession & { lastModified: number };

    // Remove Firebase-specific fields
    const { lastModified, ...session } = data;

    console.log('[LevelUp] Session loaded from Firebase:', session.sessionId);
    return session;
  } catch (error) {
    console.error('[LevelUp] Failed to load session from Firebase:', error);
    return null;
  }
}

/**
 * Deletes a level-up draft from Firestore
 * Called when session is committed or cancelled
 */
export async function deleteLevelUpSessionFromFirebase(
  characterId: string
): Promise<void> {
  if (!characterId) {
    return;
  }

  try {
    const draftRef = getLevelUpDraftRef(characterId);
    await deleteDoc(draftRef);

    console.log('[LevelUp] Session deleted from Firebase');
  } catch (error) {
    console.error('[LevelUp] Failed to delete session from Firebase:', error);
    // Don't throw - deletion failure shouldn't break the flow
  }
}

/**
 * Checks if a character has an existing level-up draft in Firestore
 */
export async function hasLevelUpDraftInFirebase(
  characterId: string
): Promise<boolean> {
  if (!characterId) {
    return false;
  }

  try {
    const draftRef = getLevelUpDraftRef(characterId);
    const snapshot = await getDoc(draftRef);
    return snapshot.exists();
  } catch (error) {
    console.error('[LevelUp] Failed to check for draft:', error);
    return false;
  }
}

/**
 * Syncs a session between localStorage and Firebase
 * Prefers Firebase as source of truth if both exist
 */
export async function syncLevelUpSession(
  characterId: string,
  localSession: LevelUpSession | null
): Promise<LevelUpSession | null> {
  // Try to load from Firebase first
  const firebaseSession = await loadLevelUpSessionFromFirebase(characterId);

  // If both exist, prefer the most recently modified
  if (firebaseSession && localSession) {
    const firebaseTime = firebaseSession.createdAt;
    const localTime = localSession.createdAt;

    if (firebaseTime > localTime) {
      console.log('[LevelUp] Using Firebase session (newer)');
      return firebaseSession;
    } else {
      console.log('[LevelUp] Using local session (newer)');
      return localSession;
    }
  }

  // Return whichever exists
  return firebaseSession || localSession;
}
